import { useEffect, useState } from "react";
import api, { apiError, rows } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShieldCheck, Lock } from "lucide-react";
import { ConfirmDialog, Modal } from "@/components/panel/ui";
import { Badge, Button, Checkbox, DataTable, Field, Input, PageHeader, Select } from "@/components/ui-kit";

const ROLE_LABEL = { admin: "Administrador", vendedor: "Vendedor", balcao: "Balcão" };

const ROLE_HINT = {
  admin: "Alcança as telas administrativas. Use para quem cuida de cadastro, estoque, financeiro ou fiscal.",
  vendedor: "Opera o balcão e acompanha as próprias comissões.",
  balcao: "Opera o balcão, sem acesso a comissões.",
};

function ProfileForm({ profile, groups, deliverable, actions, onClose, onSaved }) {
  const [form, setForm] = useState(profile
    ? {
      name: profile.name, base_role: profile.base_role,
      permissions: [...(profile.permissions || [])],
      max_discount_percent: profile.max_discount_percent ?? "",
    }
    : { name: "", base_role: "vendedor", permissions: [], max_discount_percent: "" });
  const [saving, setSaving] = useState(false);

  /**
   * O que o painel daquele papel mostra — hoje, o catálogo inteiro.
   *
   * A lista continua vindo do servidor em vez de ser assumida aqui: se um dia
   * algum painel voltar a não ter uma tela, o editor volta a desabilitá-la
   * sozinho, em vez de prometer o que não abre.
   */
  const entregaveis = deliverable[form.base_role] || [];
  const entrega = (key) => entregaveis.includes(key);
  // Chave que funciona mas não vira item de menu (ex.: "orçamentos" libera o
  // botão Gerar orçamento dentro do PDV). Marcar faz efeito; só não aparece
  // como tela — e é isso que o texto ao lado precisa dizer.
  const acoes = actions[form.base_role] || {};
  const marcadasEfetivas = form.permissions.filter(entrega);
  const telasEfetivas = marcadasEfetivas.filter((k) => !acoes[k]);

  const toggle = (key) => {
    if (!entrega(key)) return;
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));
  };

  // "Marcar todas" só alcança o que aquele painel mostra: marcar o que não
  // abre era a origem do número inflado.
  const toggleGroup = (keys) => setForm((f) => {
    const uteis = keys.filter(entrega);
    const todas = uteis.every((k) => f.permissions.includes(k));
    return {
      ...f,
      permissions: todas
        ? f.permissions.filter((p) => !uteis.includes(p))
        : [...new Set([...f.permissions, ...uteis])],
    };
  });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Dê um nome ao perfil"); return; }
    if (!marcadasEfetivas.length) { toast.error("Marque ao menos uma tela que este painel mostre"); return; }
    setSaving(true);
    try {
      // Campo vazio significa "sem teto", que no banco é NULL — mandar "" faria
      // o validador numérico recusar.
      const payload = {
        ...form,
        max_discount_percent: form.max_discount_percent === "" ? null : Number(form.max_discount_percent),
      };
      if (profile) await api.patch(`/admin/profiles/${profile.id}`, payload);
      else await api.post("/admin/profiles", payload);
      toast.success(profile ? "Perfil atualizado" : "Perfil criado");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={profile ? `Editar ${profile.name}` : "Novo perfil de acesso"} maxWidth="max-w-2xl" testId="profile-form-dialog">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Nome do perfil" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex.: Estoquista" data-testid="profile-form-name" />
          </Field>
          <Field label="Entra como" hint="Define o painel de entrada; as telas vêm das permissões abaixo.">
            <Select value={form.base_role} onChange={(e) => setForm({ ...form, base_role: e.target.value })}
              disabled={profile?.is_system} data-testid="profile-form-role"
              className="disabled:bg-slate-100 disabled:text-slate-400">
              {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </Field>
        </div>
        <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">{ROLE_HINT[form.base_role]}</p>

        <Field
          label="Desconto máximo no PDV (%)"
          hint="Em branco, sem limite. O servidor recusa a venda que passar do teto — não adianta contornar pela tela."
        >
          <Input
            type="number" min="0" max="100" step="0.5"
            value={form.max_discount_percent}
            onChange={(e) => setForm({ ...form, max_discount_percent: e.target.value })}
            placeholder="sem limite"
            mono
            className="w-40"
            data-testid="profile-form-max-discount"
          />
        </Field>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Telas liberadas <span className="text-slate-400 normal-case font-normal">({telasEfetivas.length} marcadas)</span>
          </p>
          {/* Os três painéis servem o mesmo catálogo: o que é marcado aqui
              aparece no menu de quem entra, seja qual for o papel. O aviso
              lembra que a marcação é o acesso — não há mais um segundo portão
              por papel para segurar um engano. */}
          <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 mb-3" data-testid="profile-form-ceiling">
            Toda tela marcada aparece no menu de quem entra com este perfil, no painel de {ROLE_LABEL[form.base_role]}.
            O servidor confere a mesma marcação — é ela que dá ou nega o acesso.
          </p>
          <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
            {Object.entries(groups).map(([section, telas]) => {
              const keys = Object.keys(telas);
              const uteis = keys.filter(entrega);
              const todas = uteis.length > 0 && uteis.every((k) => form.permissions.includes(k));
              return (
                /* Grupo de caixas de seleção = fieldset/legend: é assim que o leitor
                   de tela anuncia "Operação" antes de cada tela da seção.
                   A legenda vai em sr-only porque uma `legend` visível vira a
                   "legenda renderizada" e o navegador deixa de pintar a borda do
                   fieldset atrás dela — com largura total, a borda de cima do
                   quadro sumia inteira. Em sr-only (position:absolute) ela sai
                   dessa regra, e o título visível repete o texto marcado como
                   aria-hidden para não ser anunciado duas vezes. */
                <fieldset key={section} className="border border-slate-100 rounded-lg p-3">
                  <legend className="sr-only">{section}</legend>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-700" aria-hidden>{section}</p>
                    {/* Seção inteira fora do painel não ganha "marcar todas":
                        o botão não teria o que marcar. */}
                    {uteis.length > 0 && (
                      <Button variant="link" size="sm" onClick={() => toggleGroup(keys)}
                        aria-label={`${todas ? "Desmarcar" : "Marcar"} todas as telas de ${section}`}>
                        {todas ? "Desmarcar todas" : "Marcar todas"}
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {Object.entries(telas).map(([key, label]) => {
                      const fora = !entrega(key);
                      const acao = acoes[key];
                      return (
                        <Checkbox
                          key={key}
                          label={
                            fora ? <span className="text-slate-400">{label} <span className="text-[11px]">— fora deste painel</span></span>
                              : acao ? <span>{label} <span className="text-[11px] text-slate-400">— ação, não vira menu</span></span>
                                : label
                          }
                          checked={!fora && form.permissions.includes(key)}
                          onChange={() => toggle(key)}
                          disabled={fora}
                          title={acao || undefined}
                          aria-label={fora
                            ? `${label} — o painel de ${ROLE_LABEL[form.base_role]} não tem esta tela`
                            : acao ? `${label} — ${acao}`
                              : `Liberar a tela ${label}`}
                          data-testid={`profile-perm-${key}`}
                          className={fora ? "py-1 cursor-not-allowed" : "py-1"}
                        />
                      );
                    })}
                  </div>
                </fieldset>
              );
            })}
          </div>
        </div>

        <Button onClick={save} loading={saving} size="lg" block data-testid="profile-form-save">
          {saving ? "Salvando..." : (profile ? "Salvar alterações" : "Criar perfil")}
        </Button>
      </div>
    </Modal>
  );
}

export default function Profiles() {
  const [profiles, setProfiles] = useState([]);
  const [groups, setGroups] = useState({});
  // Quais telas o painel de cada papel entrega — o servidor é a fonte, para o
  // editor não precisar conhecer o mapa de menus.
  const [deliverable, setDeliverable] = useState({});
  const [actions, setActions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/profiles").then((r) => setProfiles(rows(r))),
      api.get("/admin/profiles/permissions").then((r) => {
        setGroups(r.data.groups || {});
        setDeliverable(r.data.deliverable || {});
        setActions(r.data.actions || {});
      }),
    ]).then(() => setError(false)).catch(() => setError(true)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (p) => {
    try { await api.delete(`/admin/profiles/${p.id}`); toast.success(`Perfil ${p.name} excluído`); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setRemoving(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-profiles-page">
      <PageHeader
        eyebrow="Sistema"
        title="Perfis de Acesso"
        icon={ShieldCheck}
        description="Cada perfil é o conjunto de telas de uma função. Tirar uma tela daqui some com ela do menu de todos os usuários ligados ao perfil."
        actions={(
          <Button icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }} data-testid="profiles-new-btn">
            Novo perfil
          </Button>
        )}
      />

      <DataTable
        rowTestId="profile-row"
        caption="Perfis de acesso cadastrados"
        rows={profiles}
        rowKey={(p) => p.id}
        loading={loading}
        error={error}
        onRetry={load}
        empty={{
          icon: ShieldCheck,
          title: "Nenhum perfil cadastrado",
          desc: "Crie perfis para controlar o que cada função enxerga.",
          action: <Button icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }}>Criar primeiro perfil</Button>,
          testId: "profiles-empty",
        }}
        columns={[
          {
            key: "perfil", header: "Perfil",
            cell: (p) => (
              <p className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                <ShieldCheck size={14} className="text-brand-500 shrink-0" aria-hidden /> {p.name}
                {/* Badge não repassa `title`, então o span guarda a explicação de
                    por que essa linha não tem botão de excluir. */}
                {p.is_system && (
                  <span title="Perfil de sistema: não pode ser excluído">
                    <Badge><Lock size={10} aria-hidden /> Sistema</Badge>
                  </span>
                )}
              </p>
            ),
          },
          { key: "entra", header: "Entra como", className: "text-slate-600", cell: (p) => ROLE_LABEL[p.base_role] },
          {
            key: "telas", header: "Telas", align: "center", mono: true, className: "text-slate-600",
            // Itens de MENU, não marcações: `orcamentos` fora do admin é o
            // botão de orçamento no PDV, não uma tela.
            cell: (p) => p.screens_count ?? p.permissions?.length ?? 0,
          },
          { key: "usuarios", header: "Usuários", align: "center", mono: true, className: "text-slate-600", cell: (p) => p.users_count ?? 0 },
          {
            key: "acoes", header: "Ações", align: "center", className: "whitespace-nowrap",
            cell: (p) => (
              <>
                <Button variant="ghost" size="iconSm" onClick={() => { setEditing(p); setShowForm(true); }}
                  data-testid="profile-edit-btn" aria-label={`Editar telas de ${p.name}`} title="Editar telas"
                  className="hover:text-brand-600"><Pencil size={15} /></Button>
                {!p.is_system && (
                  <Button variant="ghost" size="iconSm" onClick={() => setRemoving(p)}
                    data-testid="profile-remove-btn" aria-label={`Excluir perfil ${p.name}`} title="Excluir perfil"
                    className="hover:text-red-500"><Trash2 size={15} /></Button>
                )}
              </>
            ),
          },
        ]}
      />

      {showForm && (
        <ProfileForm profile={editing} groups={groups} deliverable={deliverable} actions={actions} onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }} />
      )}

      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => remove(removing)}
        title="Excluir perfil"
        message={removing ? `Excluir o perfil "${removing.name}"? Só é possível se nenhum usuário estiver usando ele.` : ""}
        confirmLabel="Excluir"
      />
    </div>
  );
}
